// React & React Hooks
import { useContext, useEffect, useState } from "react";

// Third-party libraries
import { useTranslation } from "react-i18next";
import { Box, Card, Grid, Stack, Typography } from "@mui/material";
import { chartsTooltipClasses } from "@mui/x-charts";
import { BarChart } from "@mui/x-charts/BarChart";
import { styled } from "@mui/material/styles";

// Project constants
import urlConstant from "../../constants/urlConstants";
import dataConstant from "../../constants/dataConstants";

// Shared / reusable components
import DataCardGrid from "../../shared-components/DataCardGrid/DataCardGrid";

// Services / utilities
import { httpPost, httpGet } from "../../services/commonService";

// Context / Providers
import { AuthenticationContext } from "@cloudmlm/cloudmlm-auth";

const CustomBarChart = styled(BarChart)(({ theme }) => ({
  "& .MuiBarElement-root": {
    width: '12px !important',
    rx: "6px !important",
    ry: "6px !important",
    [theme.breakpoints.down("xl")]: {
      transform: "translateX(11px) !important",
    },
    [theme.breakpoints.down("sm")]: {
      transform: "translateX(9px) !important",
    },
     "@media (max-width:2560px)": {
      transform: "translateX(18px) !important",
    },            
    "@media (max-width:1440px)": {
      transform: "translateX(12px) !important",
    },
     "@media (max-width:1024px)": {
      transform: "translateX(4px) !important",
    },
    "@media (max-width:425px)": {
      transform: "translateX(12px) !important",
    },
    "@media (max-width:768px)": {
      transform: "translateX(12px) !important",
    },    
    "@media (max-width:375px)": {
      transform: "translateX(6px) !important",
    },
    "@media (max-width:320px)": {
      transform: "translateX(1px) !important",
    },
  },
  "& .MuiChartsAxisHighlight-root": {
    display: "none !important",
  },
}));

const MIN_BAR_VALUE = 0;
const fillChartData = (arr) => {
  if (!Array.isArray(arr)) return Array(5).fill({ display: MIN_BAR_VALUE, real: 0 });
  return Array(5)
    .fill(0)
    .map((_, i) => {
      const real = Number(arr[i]);
      const isValidNumber = !isNaN(real);
      return {
        display: isValidNumber && real > 0 ? real : MIN_BAR_VALUE,
        real: isValidNumber ? real : 0,
      };
    });
};
const getYAxisMax = (dataArr) => {
  const maxReal = Math.max(...dataArr.map(d => d.real), 0);
  return maxReal > 1 ? Math.ceil(maxReal * 1) : 1;
};

function DualTeamBonusVolume() {
  const { t } = useTranslation();
  const { accessToken, impersonateEmail } = useContext(AuthenticationContext);

  const [state, setState] = useState({
    currentDualTeamBonusVolumeStatistics: null,
    currDTBVolSectionVisible: false,
    currDTBVolDatePeriod: "",
    currDTBVolLeftLeg: null,
    currDTBVolLeftLegMyPerWeekProgress: [],
    currDTBVolLeftLegCommVolWeekProgress: [],
    currDTBVolRightLeg: null,
    currDTBVolRightLegMyPerWeekProgress: [],
    currDTBVolRightLegCommVolWeekProgress: [],
  });

  const leftLegMyPerWeekData = fillChartData(state.currDTBVolLeftLegMyPerWeekProgress);
  const leftLegCommVolWeekData = fillChartData(state.currDTBVolLeftLegCommVolWeekProgress);
  const rightLegMyPerWeekData = fillChartData(state.currDTBVolRightLegMyPerWeekProgress);
  const rightLegCommVolWeekData = fillChartData(state.currDTBVolRightLegCommVolWeekProgress);

  const columns = [
    {
      key: "PeriodStartDate",
      label: t("DualTeamBonusRpt.StartDate"),
      type: "date",
      isFilterVisible: true,
    },
    {
      key: "NewDualTeamPaidRankName",
      label: t("DualTeamBonusRpt.Rank"),
      type: "text",
      isFilterVisible: true,
    },
    {
      key: "PSLeft",
      label: t("DualTeamBonusRpt.PSLeft"),
      type: "number",
      isFilterVisible: false,
    },
    {
      key: "PSRight",
      label: t("DualTeamBonusRpt.PSRight"),
      type: "number",
      isFilterVisible: false,
    },
    {
      key: "CommissionableVolLT",
      label: t("DualTeamBonusRpt.CommissionableVolLT"),
      type: "number",
      isFilterVisible: false,
    },
    {
      key: "CommissionableVolRT",
      label: t("DualTeamBonusRpt.CommissionableVolRT"),
      type: "number",
      isFilterVisible: false,
    },
    {
      key: "DualTeamBonusCommission",
      label: t("DualTeamBonusRpt.DualTeamCommission"),
      type: "money",
      isFilterVisible: false,
    },
    {
      key: "MatchingBonusCommission",
      label: t("DualTeamBonusRpt.MatchingBonus"),
      type: "money",
      isFilterVisible: false,
    },
  ];

  useEffect(() => {
    DualTeamBonusVolumeStatisticsReportByUserId();
  }, []);

  const DualTeamBonusVolumeStatisticsReportByUserId = async () => {
    const url = `${urlConstant.Reports.DualTeamBonusVolumeStatisticsReportByUserId}`;
    await httpGet(url, true, true, accessToken, impersonateEmail)
      .then((response) => {
        const responseData = response.Value.Data;
        setState((prevState) => ({
          ...prevState,
          currentDualTeamBonusVolumeStatistics:
            responseData.CurrentDualTeamBonusVolumeStatistics,
        }));
        if (
          responseData.CurrentDualTeamBonusVolumeStatistics != null &&
          responseData.CurrentDualTeamBonusVolumeStatistics.length > 0
        ) {
          setState((prevState) => ({
            ...prevState,
            currDTBVolSectionVisible:
              responseData.CurrentDualTeamBonusVolumeStatistics[0]
                .SectionVisible,
            currDTBVolDatePeriod: `${responseData.CurrentDualTeamBonusVolumeStatistics[0].StartDate} - ${responseData.CurrentDualTeamBonusVolumeStatistics[0].EndDate}`,
            currDTBVolLeftLeg:
              responseData.CurrentDualTeamBonusVolumeStatistics[0],
            currDTBVolRightLeg:
              responseData.CurrentDualTeamBonusVolumeStatistics[1],
            currDTBVolLeftLegMyPerWeekProgress: [
              responseData.CurrentDualTeamBonusVolumeStatistics[0].MyPerWeek1,
              responseData.CurrentDualTeamBonusVolumeStatistics[0].MyPerWeek2,
              responseData.CurrentDualTeamBonusVolumeStatistics[0].MyPerWeek3,
              responseData.CurrentDualTeamBonusVolumeStatistics[0].MyPerWeek4,
              responseData.CurrentDualTeamBonusVolumeStatistics[0].MyPerWeek5,
            ],
            currDTBVolLeftLegCommVolWeekProgress: [
              responseData.CurrentDualTeamBonusVolumeStatistics[0].CommVolWeek1,
              responseData.CurrentDualTeamBonusVolumeStatistics[0].CommVolWeek2,
              responseData.CurrentDualTeamBonusVolumeStatistics[0].CommVolWeek3,
              responseData.CurrentDualTeamBonusVolumeStatistics[0].CommVolWeek4,
              responseData.CurrentDualTeamBonusVolumeStatistics[0].CommVolWeek5,
            ],
            currDTBVolRightLegMyPerWeekProgress: [
              responseData.CurrentDualTeamBonusVolumeStatistics[1].MyPerWeek1,
              responseData.CurrentDualTeamBonusVolumeStatistics[1].MyPerWeek2,
              responseData.CurrentDualTeamBonusVolumeStatistics[1].MyPerWeek3,
              responseData.CurrentDualTeamBonusVolumeStatistics[1].MyPerWeek4,
              responseData.CurrentDualTeamBonusVolumeStatistics[1].MyPerWeek5,
            ],
            currDTBVolRightLegCommVolWeekProgress: [
              responseData.CurrentDualTeamBonusVolumeStatistics[1].CommVolWeek1,
              responseData.CurrentDualTeamBonusVolumeStatistics[1].CommVolWeek2,
              responseData.CurrentDualTeamBonusVolumeStatistics[1].CommVolWeek3,
              responseData.CurrentDualTeamBonusVolumeStatistics[1].CommVolWeek4,
              responseData.CurrentDualTeamBonusVolumeStatistics[1].CommVolWeek5,
            ],
          }));
        }
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const fetchDataFunction = async ({
    page,
    pageSize,
    search,
    filters,
    sortKey,
    sortDirection,
  }) => {
    const getDualTeamVolumeList = urlConstant.Reports.DualTeamBonusVolumeReport;
    const pagingInfo = {
      PageNumber: page,
      RowsPerPage: pageSize,
      CommonSearchText: search,
      SortColumns: {
        SortColumnName: sortKey,
        SortOrder: sortDirection,
      },
      Filter: {
        RankName: filters?.NewDualTeamPaidRankName,
        PeriodStartDate: filters?.PeriodStartDate,
      },
    };
    const requestBody = {
      PagingInfoVM: pagingInfo,
    };
    try {
      const response = await httpPost(
        getDualTeamVolumeList,
        requestBody,
        true,
        false,
        accessToken,
        impersonateEmail
      );
      if (response?.Value?.Status === dataConstant.APIResponseStatus.Success) {
        return {
          data: response.Value.Data.Data,
          totalCount: response.Value.Data.TotalCount,
        };
      } else {
        return { data: [], totalCount: 0 };
      }
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <>
      <Box flexDirection={"column"} gap={2} display={"flex"}>
        <Typography
          color="secondary.light"
          display={{ xs: "flex", sm: "none" }}
          variant="h4"
          fontWeight={600}
          fontSize={"24px"}
        >
          {t("Reports")}
        </Typography>
        <Box width={"100%"}>
          <Card
            sx={(theme) => ({
              p: {
                xs: "20px",
                sm: "25px 30px",
              },
              background: theme.palette.background.cardGradient,
              border: "1px solid var(--headerbg)",
              borderRadius: "20px",
              height: "100%",
            })}
          >
            <Grid
              container
              spacing={0}
              sx={{ flexGrow: 1 }}
              display={!state.currDTBVolSectionVisible ? "none" : ""}
            >
              <Grid size={{ xs: 12 }}>
                <Stack
                  alignItems={"center"}
                  gap={1}
                  justifyContent={"space-between"}
                  sx={{
                    flexDirection: {
                      xs: "column",
                      sm: "row",
                    },
                  }}
                >
                  <Typography color="secondary.light" variant="h4">
                    {t("Dashboard.CurrentDualTeamBonusVolume")}
                  </Typography>
                  <Stack
                    direction={"row"}
                    alignItems={"center"}
                    flexWrap={"wrap"}
                    gap={2}
                  >
                    <Stack direction={"row"} alignItems={"center"} gap={1}>
                      <img src="/icons/calendar.svg" alt="user" />
                      <Card className="bg-dark" sx={{ py: 1, px: 2 }}>
                        <Typography color="secondary.main" variant="body1">
                          {state.currDTBVolDatePeriod}
                        </Typography>
                      </Card>
                    </Stack>
                  </Stack>
                </Stack>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Card
                      sx={(theme) => ({
                        p: "20px",
                        mt: "20px",
                        background: theme.palette.primary.lightdark,
                        borderRadius: "20px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexDirection: "column",
                        gap: 2,
                      })}
                    >
                      <Stack
                        gap={1.5}
                        display={"flex"}
                        justifyContent={"center"}
                        textAlign={"center"}
                        direction={"row"}
                        alignItems={"center"}
                      >
                        <Typography
                          color="common.white"
                          sx={{}}
                          variant="h4"
                          fontWeight={600}
                        >
                          {t("Dashboard.Left")}
                        </Typography>
                        <img src="/icons/left-tree.svg" alt="user" />
                      </Stack>
                      <Stack
                        gap={1}
                        alignItems={"center"}
                        width={"100%"}
                        sx={{
                          flexDirection: {
                            xs: "column",
                            sm: "row",
                          },
                        }}
                      >
                        <Stack sx={{ width: "100%" }}>
                          <Box sx={{ position: "relative", width: "100%", height: 300}}>                            
                            <CustomBarChart
                              layout="vertical"
                              className="cm-chartbar"
                              xAxis={[
                                {
                                  id: "barCategories",
                                  data: ["W1", "W2", "W3", "W4", "W5"],
                                  scaleType: "band",
                                  disableLine: true,
                                  disableTicks: true,
                                },
                              ]}
                              yAxis={[
                                {
                                  id: "y-axis",
                                  min: 0,
                                  max: getYAxisMax(leftLegMyPerWeekData),
                                  disableLine: true,
                                  disableTicks: true,
                                  tickLabelStyle: { display: "none" },
                                },
                              ]}
                              grid={{ horizontal: false, vertical: false }}
                              series={[
                                {
                                  data: Array(5).fill(getYAxisMax(leftLegMyPerWeekData)),
                                  color: "#44454a",
                                  borderRadius: "8px",
                                  barWidth: 6,
                                  barCategoryGap: 8,
                                },
                              ]}                              
                              height={300}
                              sx={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                width: "100%",
                                pointerEvents: "none",
                                zIndex: 1,
                              }}
                            />                            
                            <CustomBarChart
                              layout="vertical"
                              className="cm-chartbar"
                              xAxis={[
                                {
                                  id: "barCategories",
                                  data: ["W1", "W2", "W3", "W4", "W5"],
                                  scaleType: "band",
                                  disableLine: true,
                                  disableTicks: true,
                                },
                              ]}
                              yAxis={[
                                {
                                  id: "y-axis",
                                  min: 0,
                                  max: getYAxisMax(leftLegMyPerWeekData),
                                  disableLine: true,
                                  disableTicks: true,
                                  tickLabelStyle: { display: "none" },
                                },
                              ]}
                              grid={{ horizontal: false, vertical: false }}
                              series={[
                                {
                                  data: leftLegMyPerWeekData.map(d => d.display),
                                  color: "var(--primary-main)",
                                  borderRadius: "8px",
                                  barWidth: 6,
                                  barCategoryGap: 8,
                                  valueFormatter: (val, context) => {                                    
                                    return leftLegMyPerWeekData[context.dataIndex]?.real ?? 0;
                                  },
                                },
                              ]}                             
                              height={300}                              
                              sx={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                width: "100%",
                                zIndex: 2,
                                pointerEvents: "auto",                             
                              }}
                              slotProps={{
                                tooltip: {
                                  trigger: "item",
                                  popper: {
                                    sx: {
                                      "& .MuiChartsTooltip-paper": {
                                        backgroundColor: "transparent",
                                        boxShadow: "none",
                                      },
                                      [`& .${chartsTooltipClasses.root}`]: {
                                        border: "none",
                                        backgroundColor: "transparent",
                                      },
                                    },
                                  },
                                },
                              }}
                            />
                          </Box>
                          <Stack gap={0} textAlign={"center"}>
                            <Typography
                              color="common.white"
                              sx={{ textTransform: "uppercase" }}
                              variant="h3"
                              fontWeight={600}
                            >
                              {state.currDTBVolLeftLeg?.MyPersonalCount}
                            </Typography>
                            <Typography
                              color="secondary.text"
                              sx={{ fontSize: "12px" }}
                              variant="body2"
                              fontWeight={600}
                            >
                              {t("Dashboard.MyPersonals")}
                            </Typography>
                          </Stack>
                        </Stack>
                        <Stack sx={{ width: "100%" }}>
                          <Box sx={{ position: "relative", width: "100%", height: 300}}>                            
                            <CustomBarChart
                              layout="vertical"
                              className="cm-chartbar"
                              xAxis={[
                                {
                                  id: "barCategories",
                                  data: ["W1", "W2", "W3", "W4", "W5"],
                                  scaleType: "band",
                                  disableLine: true,
                                  disableTicks: true,
                                },
                              ]}
                              yAxis={[
                                {
                                  id: "y-axis",
                                  min: 0,
                                  max: getYAxisMax(leftLegCommVolWeekData),
                                  disableLine: true,
                                  disableTicks: true,
                                  tickLabelStyle: { display: "none" },
                                },
                              ]}
                              grid={{ horizontal: false, vertical: false }}
                              series={[
                                {
                                  data: Array(5).fill(getYAxisMax(leftLegCommVolWeekData)),
                                  color: "#44454a",
                                  borderRadius: "8px",
                                  barWidth: 6,
                                  barCategoryGap: 8,
                                },
                              ]}                              
                              height={300}
                              sx={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                width: "100%",
                                pointerEvents: "none",
                                zIndex: 1,
                              }}
                            />                            
                            <CustomBarChart
                              layout="vertical"
                              className="cm-chartbar"
                              xAxis={[
                                {
                                  id: "barCategories",
                                  data: ["W1", "W2", "W3", "W4", "W5"],
                                  scaleType: "band",
                                  disableLine: true,
                                  disableTicks: true,
                                },
                              ]}
                              yAxis={[
                                {
                                  id: "y-axis",
                                  min: 0,
                                  max: getYAxisMax(leftLegCommVolWeekData),
                                  disableLine: true,
                                  disableTicks: true,
                                  tickLabelStyle: { display: "none" },
                                },
                              ]}
                              grid={{ horizontal: false, vertical: false }}
                              series={[
                                {
                                  data: leftLegCommVolWeekData.map(d => d.display),
                                  color: "var(--primary-main)",
                                  borderRadius: "8px",
                                  barWidth: 6,
                                  barCategoryGap: 8,
                                  valueFormatter: (val, context) => {                                    
                                    return leftLegCommVolWeekData[context.dataIndex]?.real ?? 0;
                                  },
                                },
                              ]}                             
                              height={300}                              
                              sx={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                width: "100%",
                                zIndex: 2,
                                pointerEvents: "auto",                             
                              }}
                              slotProps={{
                                tooltip: {
                                  trigger: "item",
                                  popper: {
                                    sx: {
                                      "& .MuiChartsTooltip-paper": {
                                        backgroundColor: "transparent",
                                        boxShadow: "none",
                                      },
                                      [`& .${chartsTooltipClasses.root}`]: {
                                        border: "none",
                                        backgroundColor: "transparent",
                                      },
                                    },
                                  },
                                },
                              }}
                            />
                          </Box>
                          <Stack gap={0} textAlign={"center"}>
                            <Typography
                              color="common.white"
                              sx={{ textTransform: "uppercase" }}
                              variant="h3"
                              fontWeight={600}
                            >
                              {state.currDTBVolLeftLeg?.CommissionVolume}
                            </Typography>
                            <Typography
                              color="secondary.text"
                              sx={{ fontSize: "12px" }}
                              variant="body2"
                              fontWeight={600}
                            >
                              {t("Dashboard.CommissionVolume")}
                            </Typography>
                          </Stack>
                        </Stack>
                      </Stack>
                    </Card>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Card
                      sx={(theme) => ({
                        p: "20px",
                        mt: "20px",
                        background: theme.palette.primary.lightdark,
                        borderRadius: "20px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexDirection: "column",
                        gap: 2,
                      })}
                    >
                      <Stack
                        gap={1.5}
                        display={"flex"}
                        justifyContent={"center"}
                        textAlign={"center"}
                        direction={"row"}
                        alignItems={"center"}
                      >
                        <img src="/icons/right-tree.svg" alt="user" />
                        <Typography
                          color="common.white"
                          sx={{}}
                          variant="h4"
                          fontWeight={600}
                        >
                          {t("Dashboard.Right")}
                        </Typography>
                      </Stack>
                      <Stack
                        gap={1}
                        alignItems={"center"}
                        width={"100%"}
                        sx={{
                          flexDirection: {
                            xs: "column",
                            sm: "row",
                          },
                        }}
                      >
                        <Stack sx={{ width: "100%" }}>
                          <Box sx={{ position: "relative", width: "100%", height: 300}}>                            
                            <CustomBarChart
                              layout="vertical"
                              className="cm-chartbar"
                              xAxis={[
                                {
                                  id: "barCategories",
                                  data: ["W1", "W2", "W3", "W4", "W5"],
                                  scaleType: "band",
                                  disableLine: true,
                                  disableTicks: true,
                                },
                              ]}
                              yAxis={[
                                {
                                  id: "y-axis",
                                  min: 0,
                                  max: getYAxisMax(rightLegMyPerWeekData),
                                  disableLine: true,
                                  disableTicks: true,
                                  tickLabelStyle: { display: "none" },
                                },
                              ]}
                              grid={{ horizontal: false, vertical: false }}
                              series={[
                                {
                                  data: Array(5).fill(getYAxisMax(rightLegMyPerWeekData)),
                                  color: "#44454a",
                                  borderRadius: "8px",
                                  barWidth: 6,
                                  barCategoryGap: 8,
                                },
                              ]}                              
                              height={300}
                              sx={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                width: "100%",
                                pointerEvents: "none",
                                zIndex: 1,
                              }}
                            />                            
                            <CustomBarChart
                              layout="vertical"
                              className="cm-chartbar"
                              xAxis={[
                                {
                                  id: "barCategories",
                                  data: ["W1", "W2", "W3", "W4", "W5"],
                                  scaleType: "band",
                                  disableLine: true,
                                  disableTicks: true,
                                },
                              ]}
                              yAxis={[
                                {
                                  id: "y-axis",
                                  min: 0,
                                  max: getYAxisMax(rightLegMyPerWeekData),
                                  disableLine: true,
                                  disableTicks: true,
                                  tickLabelStyle: { display: "none" },
                                },
                              ]}
                              grid={{ horizontal: false, vertical: false }}
                              series={[
                                {
                                  data: rightLegMyPerWeekData.map(d => d.display),
                                  color: "var(--primary-main)",
                                  borderRadius: "8px",
                                  barWidth: 6,
                                  barCategoryGap: 8,
                                  valueFormatter: (val, context) => {                                    
                                    return rightLegMyPerWeekData[context.dataIndex]?.real ?? 0;
                                  },
                                },
                              ]}                             
                              height={300}                              
                              sx={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                width: "100%",
                                zIndex: 2,
                                pointerEvents: "auto",                             
                              }}
                              slotProps={{
                                tooltip: {
                                  trigger: "item",
                                  popper: {
                                    sx: {
                                      "& .MuiChartsTooltip-paper": {
                                        backgroundColor: "transparent",
                                        boxShadow: "none",
                                      },
                                      [`& .${chartsTooltipClasses.root}`]: {
                                        border: "none",
                                        backgroundColor: "transparent",
                                      },
                                    },
                                  },
                                },
                              }}
                            />
                          </Box>
                          <Stack gap={0} textAlign={"center"}>
                            <Typography
                              color="common.white"
                              sx={{ textTransform: "uppercase" }}
                              variant="h3"
                              fontWeight={600}
                            >
                              {state.currDTBVolRightLeg?.MyPersonalCount}
                            </Typography>
                            <Typography
                              color="secondary.text"
                              sx={{ fontSize: "12px" }}
                              variant="body2"
                              fontWeight={600}
                            >
                              {t("Dashboard.MyPersonals")}
                            </Typography>
                          </Stack>
                        </Stack>
                        <Stack sx={{ width: "100%" }}>
                          <Box sx={{ position: "relative", width: "100%", height: 300}}>                            
                            <CustomBarChart
                              layout="vertical"
                              className="cm-chartbar"
                              xAxis={[
                                {
                                  id: "barCategories",
                                  data: ["W1", "W2", "W3", "W4", "W5"],
                                  scaleType: "band",
                                  disableLine: true,
                                  disableTicks: true,
                                },
                              ]}
                              yAxis={[
                                {
                                  id: "y-axis",
                                  min: 0,
                                  max: getYAxisMax(rightLegCommVolWeekData),
                                  disableLine: true,
                                  disableTicks: true,
                                  tickLabelStyle: { display: "none" },
                                },
                              ]}
                              grid={{ horizontal: false, vertical: false }}
                              series={[
                                {
                                  data: Array(5).fill(getYAxisMax(rightLegCommVolWeekData)),
                                  color: "#44454a",
                                  borderRadius: "8px",
                                  barWidth: 6,
                                  barCategoryGap: 8,
                                },
                              ]}                              
                              height={300}
                              sx={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                width: "100%",
                                pointerEvents: "none",
                                zIndex: 1,
                              }}
                            />                            
                            <CustomBarChart
                              layout="vertical"
                              className="cm-chartbar"
                              xAxis={[
                                {
                                  id: "barCategories",
                                  data: ["W1", "W2", "W3", "W4", "W5"],
                                  scaleType: "band",
                                  disableLine: true,
                                  disableTicks: true,
                                },
                              ]}
                              yAxis={[
                                {
                                  id: "y-axis",
                                  min: 0,
                                  max: getYAxisMax(rightLegCommVolWeekData),
                                  disableLine: true,
                                  disableTicks: true,
                                  tickLabelStyle: { display: "none" },
                                },
                              ]}
                              grid={{ horizontal: false, vertical: false }}
                              series={[
                                {
                                  data: rightLegCommVolWeekData.map(d => d.display),
                                  color: "var(--primary-main)",
                                  borderRadius: "8px",
                                  barWidth: 6,
                                  barCategoryGap: 8,
                                  valueFormatter: (val, context) => {                                    
                                    return rightLegCommVolWeekData[context.dataIndex]?.real ?? 0;
                                  },
                                },
                              ]}                             
                              height={300}                              
                              sx={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                width: "100%",
                                zIndex: 2,
                                pointerEvents: "auto",                             
                              }}
                              slotProps={{
                                tooltip: {
                                  trigger: "item",
                                  popper: {
                                    sx: {
                                      "& .MuiChartsTooltip-paper": {
                                        backgroundColor: "transparent",
                                        boxShadow: "none",
                                      },
                                      [`& .${chartsTooltipClasses.root}`]: {
                                        border: "none",
                                        backgroundColor: "transparent",
                                      },
                                    },
                                  },
                                },
                              }}
                            />
                          </Box>
                          <Stack gap={0} textAlign={"center"}>
                            <Typography
                              color="common.white"
                              sx={{ textTransform: "uppercase" }}
                              variant="h3"
                              fontWeight={600}
                            >
                              {state.currDTBVolRightLeg?.CommissionVolume}
                            </Typography>
                            <Typography
                              color="secondary.text"
                              sx={{ fontSize: "12px" }}
                              variant="body2"
                              fontWeight={600}
                            >
                              {t("Dashboard.CommissionVolume")}
                            </Typography>
                          </Stack>
                        </Stack>
                      </Stack>
                    </Card>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <Stack gap="20px" sx={{ pt: "20px" }}>
              <Grid container spacing={2}>
                <Typography
                  color="secondary.light"
                  variant="h4"
                  fontWeight={600}
                >
                  {t("DualTeamBonusRpt.MonthlyDualTeamBonusVolume")}
                </Typography>
                <Grid size={{ xs: 12, md: 12, lg: 12 }}>
                  <DataCardGrid
                    onFetchData={fetchDataFunction}
                    columns={columns}
                    defaultItemsPerPage={4}
                    defaultColumnsPerGrid={2}
                    enableExport={true}
                    enablePagination={true}
                    uniqueId="commission-grid"
                  />
                </Grid>
              </Grid>
            </Stack>
          </Card>
        </Box>
      </Box>
    </>
  );
}

export default DualTeamBonusVolume;